/**
 * The detail panel for a selected tower — the screen a BTD6 player lives in.
 *
 * Three upgrade paths run as horizontal rows, because the crossover rule is a
 * per-path rule and the panel should be shaped like the rule it enforces. Each
 * row carries, without any hovering: the path's own icon, a five-cell strip
 * wearing each tier's *own* artwork, then the next purchase as a full-width
 * control with its name, its price and what it actually does. A path that
 * cannot be bought says so in words — `PATH CLOSED`, `MAX UPGRADES` — beside an
 * icon, in its own colour, rather than greying out and hoping.
 *
 * Two things here have no BTD6 equivalent and are deliberate: the `5-2-0` tier
 * readout in the header, which summarises a crossover state faster than any
 * grid can, and the live Dealt/Kills counters, which answer "was that tower
 * worth it" during the round rather than after it.
 *
 * The crossover rule and the sell formula are injected, because the tower
 * subsystem owns them. The defaults below keep the panel honest on its own.
 */

import { ECONOMY } from '@/core/config';
import type { PhosphorName } from '@/core/palette';
import type { DamageType, TargetingMode } from '@/core/types';
import {
  el, icon, iconFromId, installUiTheme, money, phosphorVar, safeIconId, setIcon,
} from './Icon';
import type { IconName } from './Icon';
import { RollingNumber } from './HUD';

// ───────────────────────────────────────────────────────── injected shapes ──

export interface UpgradeNodeView {
  readonly name: string;
  readonly icon: string;
  readonly cost: number;
  readonly description: string;
}

export interface TowerArchetypeView {
  readonly id: string;
  readonly name: string;
  readonly icon: string;
  readonly colour: PhosphorName;
  readonly paths: readonly [
    readonly UpgradeNodeView[],
    readonly UpgradeNodeView[],
    readonly UpgradeNodeView[],
  ];
}

export interface TowerStatsView {
  readonly damage: number;
  readonly damageType: DamageType;
  readonly fireRate: number;
  readonly range: number;
  readonly pierce: number;
  readonly splash: number;
  readonly critChance: number;
}

export interface TowerView {
  readonly id: number;
  readonly archetype: TowerArchetypeView;
  readonly tiers: readonly [number, number, number];
  readonly stats: TowerStatsView;
  readonly targeting: TargetingMode;
  readonly damageDealt: number;
  readonly kills: number;
  readonly invested: number;
}

export interface UpgradeRules {
  /** The Bloons crossover rule: two paths at most, one above tier 2. */
  canPurchase(tiers: readonly [number, number, number], path: 0 | 1 | 2): boolean;
  sellValue(tower: TowerView): number;
}

export interface TowerPanelState {
  tower: TowerView | null;
  cash: number;
}

export interface TowerPanelCallbacks {
  onUpgrade?(towerId: number, path: 0 | 1 | 2): void;
  onSell?(towerId: number): void;
  onTargeting?(towerId: number, mode: TargetingMode): void;
  /**
   * Pop back to the build list. The panel stays; the tower is let go, because
   * this view *is* the selection — a panel that returned to BUILD with a range
   * ring still lit on the field would be describing a state nothing on screen
   * belongs to.
   */
  onBack?(): void;
  /** Dismiss the whole panel: no detail, no shelf, no selection. */
  onClose?(): void;
}

export const DEFAULT_UPGRADE_RULES: UpgradeRules = {
  canPurchase(tiers, path) {
    const next = tiers[path] + 1;
    if (next > 5) return false;
    const others: (0 | 1 | 2)[] = ([0, 1, 2] as const).filter((p) => p !== path);
    const invested = others.filter((p) => tiers[p] > 0);
    // A third path may never be opened.
    if (tiers[path] === 0 && invested.length >= 2) return false;
    // Only one path may pass tier 2.
    if (next >= 3 && others.some((p) => tiers[p] >= 3)) return false;
    return true;
  },
  sellValue(tower) {
    return Math.floor(tower.invested * ECONOMY.sellRatio);
  },
};

// ────────────────────────────────────────────────────────────── vocabulary ──

const TARGETING_ORDER: readonly TargetingMode[] = [
  'first', 'last', 'strongest', 'weakest', 'closest', 'furthest',
];

/**
 * Six modes, six real icons. `strongest` takes the crown; `weakest` takes the
 * sprout. `closest`/`furthest` use the collapse/expand pair so they read as one
 * opposed axis rather than two unrelated marks.
 */
const TARGETING_ICON: Record<TargetingMode, IconName> = {
  first: 'arrow-right',
  last: 'arrow-left',
  strongest: 'crown',
  weakest: 'seedlings',
  closest: 'collapse',
  furthest: 'expand',
};

const TARGETING_LABEL: Record<TargetingMode, string> = {
  first: 'First',
  last: 'Last',
  strongest: 'Strong',
  weakest: 'Weak',
  closest: 'Close',
  furthest: 'Far',
};

/** One tint per path, so a tier count reads as a colour before it reads as a number. */
const PATH_COLOUR: readonly PhosphorName[] = ['cyan', 'magenta', 'yellow'];

/** Used only when a path has no upgrades to borrow an icon from. */
const PATH_FALLBACK_ICON: readonly IconName[] = ['bolt', 'search', 'cog'];

const FALLBACK_ICON: IconName = 'shapes';

/**
 * Terminal path states get an icon each, and each icon is a different *shape*,
 * so the state reads before the colour arrives. `ban` is not in the vendored
 * set; `octagon-check` is, and an octagon is as distinct from a padlock as
 * BTD6's hexagon is from its banner.
 */
const STATE_ICON = {
  maxed: 'octagon-check',
  closed: 'lock',
} as const satisfies Record<string, IconName>;

const MAXED_TEXT = 'Every tier on this path is bought.';
const CROSSOVER_TEXT = 'Crossover rule: two paths at most, and only one may pass tier 2.';

interface PathUi {
  root: HTMLElement;
  label: HTMLElement;
  headIcon: HTMLElement;
  pips: HTMLButtonElement[];
  pipIcons: HTMLElement[];
  buy: HTMLButtonElement;
  buyIcon: HTMLElement;
  buyName: HTMLElement;
  buyCost: HTMLElement;
  /** Replaces the buy control when the path is closed or finished. */
  state: HTMLElement;
  stateIcon: HTMLElement;
  stateLabel: HTMLElement;
  /** What the next upgrade does, on the panel rather than in a tooltip. */
  desc: HTMLElement;
  /**
   * What the description line says when nobody is pointing at a pip.
   *
   * The pips borrow the line to explain their own tier, so the resting text has
   * to be kept somewhere to put back. Held here rather than re-derived, because
   * a refresh can land mid-hover and the line has to come back to what the
   * refresh decided, not to what it said before it.
   */
  descRest: string;
}

export class TowerPanel {
  private root = el('div', 'panel towerpanel');
  private headIcon = el('span', 'pxi pxi-32');
  private nameNode = el('span', 'towerpanel__name');
  private tierNode = el('span', 'towerpanel__tiers num');
  private statValues = new Map<string, HTMLElement>();
  private statRows = new Map<string, HTMLElement>();
  private dmgDealt!: RollingNumber;
  private killCount!: RollingNumber;
  private paths: PathUi[] = [];
  private backBtn = el('button', 'btn towerpanel__back');
  private closeBtn = el('button', 'btn btn--icon towerpanel__close');
  private targetBtn = el('button', 'btn targeting');
  private targetIcon!: HTMLElement;
  private targetLabel = el('span', 'targeting__mode', 'First');
  private sellBtn = el('button', 'btn btn--danger');
  private sellValue = el('span', 'num', '$0');
  private scroll = el('div', 'towerpanel__scroll');
  private body!: HTMLElement;
  /** Re-reads the scroll markers when the body's box changes; update() no longer does. */
  private bodyObserver: ResizeObserver | null = null;
  /** The three boxes that change parent between the bar and the sheet. */
  private meta = el('div', 'towerpanel__meta');
  private head!: HTMLElement;
  private stats!: HTMLElement;
  private live!: HTMLElement;
  private pathWrap!: HTMLElement;
  private sheetQuery: MediaQueryList | null = null;

  private rules: UpgradeRules = DEFAULT_UPGRADE_RULES;
  private state: TowerPanelState = { tower: null, cash: 0 };
  // Null, not '': a stub tower carries an empty id, and against a '' sentinel
  // the refresh never fired and its pips kept the placeholder padlock.
  private archetypeId: string | null = null;
  /** The tower on show, so tapping a second one can be told from opening the first. */
  private shownId: number | null = null;
  private swapTimer = 0;
  private mounted = false;

  constructor(private readonly cb: TowerPanelCallbacks = {}) {}

  /**
   * The overlay box, for the panel drag in `main.ts`.
   *
   * This panel is drawn over the shop rail rather than beside it, so wherever
   * the player puts that rail is where its upgrades have to open. It is the
   * only reason anything outside this file needs the element.
   */
  get element(): HTMLElement {
    return this.root;
  }

  mount(root: HTMLElement): void {
    if (this.mounted) return;
    this.mounted = true;
    installUiTheme();

    /*
     * The nav pair, and the reason this panel is no longer a second window.
     *
     * The shop and this panel were two slabs of chrome that happened to be
     * drawn at the same coordinates in two layouts out of three, so the product
     * already behaved as one surface — by occlusion, which tells the player
     * nothing about how to get back. Now it is a view stack: BUILD is the root,
     * this is the pushed view, and the two controls below are the only ways out
     * of it.
     *
     * `Back` leads, `Close` trails, which is where a phone has put them for
     * fifteen years. Back carries the destination's name rather than an arrow
     * alone — an arrow at the top of a panel is as easily read as "previous
     * tower".
     *
     * Two controls, one row. Stacked they were a full-height strip down the
     * leading edge of the bar holding 56px of button — the panel's tallest
     * column spent on its two rarest presses. The row is the head of the meta
     * column now, and the height it gave back went to the paths.
     */
    const nav = el('div', 'towerpanel__nav');
    this.backBtn.type = 'button';
    this.backBtn.append(
      icon('angle-left', { size: 12, role: 'uiAccent' }),
      el('span', undefined, 'Build'),
    );
    this.backBtn.setAttribute('aria-label', 'Back to the build list');
    this.backBtn.dataset.tip = 'Back to the tower shelf. [Esc]';
    this.backBtn.addEventListener('click', this.onBack);

    this.closeBtn.type = 'button';
    this.closeBtn.setAttribute('aria-label', 'Close the panel');
    this.closeBtn.dataset.tip = 'Put the whole panel away.';
    this.closeBtn.append(icon('times', { size: 12 }));
    this.closeBtn.addEventListener('click', this.onClose);
    nav.append(this.backBtn, this.closeBtn);

    const head = el('div', 'towerpanel__head');
    head.append(this.headIcon, this.nameNode, this.tierNode);

    const stats = el('div', 'towerpanel__stats');
    stats.append(
      this.statRow('damage', 'fire', 'Damage'),
      this.statRow('rate', 'clock', 'Rate'),
      this.statRow('range', 'circle-notch', 'Range'),
      this.statRow('pierce', 'merge', 'Pierce'),
      this.statRow('splash', 'sparkles', 'Splash'),
      this.statRow('crit', 'star', 'Crit'),
    );

    // Its own modifier: the two live counters and the six reference stats are
    // the same box drawn twice, and the narrowest bar in the matrix has to be
    // able to tell them apart — it keeps one of them and gives the height to
    // the three paths. See the compact-landscape block in `ui.css`.
    const live = el('div', 'towerpanel__stats towerpanel__stats--live');
    const dmgRow = this.statRow('dealt', 'chart-line', 'Dealt');
    const killRow = this.statRow('kills', 'hockey-mask', 'Kills');
    live.append(dmgRow, killRow);
    this.dmgDealt = new RollingNumber(this.statValues.get('dealt')!, compact);
    this.killCount = new RollingNumber(this.statValues.get('kills')!, compact);

    const pathWrap = el('div', 'paths');
    for (let p = 0; p < 3; p++) pathWrap.append(this.buildPath(p as 0 | 1 | 2));

    this.targetIcon = icon('arrow-right', { size: 12, role: 'uiAccent', glow: 0.6 });
    this.targetBtn.type = 'button';
    this.targetBtn.dataset.tip = 'Targeting priority — click or [T] to cycle, shift to go back.';
    this.targetBtn.append(this.targetIcon, this.targetLabel);
    this.targetBtn.addEventListener('click', this.onTargeting);

    this.sellBtn.type = 'button';
    this.sellBtn.dataset.tip = `Sell this tower for ${Math.round(ECONOMY.sellRatio * 100)}% of what you put in.`;
    this.sellBtn.append(icon('box-usd', { size: 12, role: 'uiDanger' }), this.sellValue);
    this.sellBtn.addEventListener('click', this.onSell);

    const foot = el('div', 'towerpanel__foot');
    foot.append(this.targetBtn, this.sellBtn);

    /*
     * What the tower *is*, and the one-off things a player might do to it.
     *
     * Everything in this panel that is not a path lives in this column, and the
     * split is by how often a hand reaches for it. The three paths are the
     * reason the panel opens and they are pressed every round; targeting is set
     * once per tower; sell is pressed once if ever; Dealt/Kills is read and
     * never pressed at all. Those three used to bracket the paths — nav down
     * the leading edge, counters and actions down the trailing one — so the
     * frequent thing was the thing being squeezed, and at 1120px of tube the
     * squeeze became a pan: three 168px columns, and Dealt/Kills off the end of
     * a scroller whose leading edge then cut the first path's description in
     * half. One column of rare-or-passive, one field of paths.
     *
     * The stack order is the reading order: who you are, what you are, how you
     * are doing, what you can do about it.
     */
    this.head = head;
    this.stats = stats;
    this.live = live;
    this.pathWrap = pathWrap;

    /*
     * Everything above the foot goes in a box of its own so the foot can stop
     * scrolling away.
     *
     * On a phone this panel is a 656px document in a 318px sheet and the foot
     * is the last row of it — sell and targeting sat 351px below the fold,
     * behind two full-panel swipes, with no scrollbar and nothing to say the
     * sheet continued. Sticky was the cheaper fix and the wrong one: a sticky
     * bar overlays the rows it covers, which is a real overlap and which
     * `tools/viewsweep.mjs` correctly reported 57 times. Taking the foot out of
     * the scroller instead means there is nothing for it to overlap at any
     * scroll position.
     *
     * The wrapper is structural only. Which of the two boxes actually scrolls
     * is a stylesheet decision, and in the wide rail it is still the panel, so
     * that composition is byte-for-byte what it was.
     */
    const body = el('div', 'towerpanel__body');
    /*
     * The scroller's box, which is not the scroller — the same split
     * `.shop__tray` makes and for the same reason. The two veils that say the
     * document continues have to stay put while the document moves past them,
     * so they hang off this wrapper rather than off the box that scrolls.
     */
    this.scroll.append(
      body,
      el('div', 'towerpanel__veil towerpanel__veil--start'),
      el('div', 'towerpanel__veil towerpanel__veil--end'),
    );
    this.body = body;
    body.addEventListener('scroll', this.onBodyScroll, { passive: true });
    /*
     * Size changes re-read the markers; update() does not. Driving this from
     * update() via queueMicrotask forced four layout reads per frame — the
     * body's height only actually changes when its content or box does, which
     * is exactly what a ResizeObserver reports.
     */
    if (typeof ResizeObserver !== 'undefined') {
      this.bodyObserver = new ResizeObserver(this.onBodyScroll);
      this.bodyObserver.observe(body);
    }

    /*
     * Four boxes, and the DOM order is the order a keyboard walks: the two ways
     * out, what the tower is, the three paths, the two actions.
     *
     * The meta column is assembled out of three of them by the stylesheet
     * rather than by a wrapper, because the phone sheet needs the same four in
     * a single column with the paths in the middle — nav at the top, foot at
     * the bottom, and neither of them scrolling. One DOM, two grids.
     */
    this.root.append(nav, this.meta, this.scroll, foot);
    /*
     * And which of the two shapes the three tower boxes are in right now.
     *
     * This is the one thing the stylesheet cannot do on its own. In the bar
     * they belong to the meta column, outside the scroller, because a tower's
     * name and numbers must not pan away when the paths do. In the sheet they
     * belong *inside* the scroller, above the paths, because the deck is 202px
     * tall on a tablet and anything pinned there is taken off a document that
     * already has to scroll — pinned, they left the three paths eleven pixels.
     *
     * The query is the same one the sheet's own block is written against, so
     * the two can never disagree about which layout is up.
     */
    this.sheetQuery = window.matchMedia('(max-aspect-ratio: 13/10)');
    this.sheetQuery.addEventListener('change', this.onLayout);
    this.onLayout();

    this.root.hidden = true;
    root.append(this.root);
  }

  setRules(rules: Partial<UpgradeRules>): void {
    this.rules = { ...DEFAULT_UPGRADE_RULES, ...rules };
    if (this.mounted) this.update(this.state);
  }

  update(state: TowerPanelState): void {
    this.state = state;
    if (!this.mounted) return;
    const tower = state.tower;
    this.root.hidden = tower === null;
    if (!tower) {
      this.shownId = null;
      return;
    }
    /*
     * Tapping a second tower while the first one's detail is open swaps the
     * contents in place. It must not look like the same push the panel makes
     * when it opens — that reads as a return to BUILD and back out again, which
     * is a trip the player did not take. A short re-key on the body says
     * "different tower, same view", and it is a different animation from the
     * push for exactly that reason.
     */
    if (this.shownId !== null && this.shownId !== tower.id) {
      this.root.classList.remove('is-swapping');
      void this.root.offsetWidth; // restart the animation on a rapid second tap.
      this.root.classList.add('is-swapping');
      window.clearTimeout(this.swapTimer);
      // 20ms past the 120ms animation, so the class is gone before a re-tap.
      this.swapTimer = window.setTimeout(this.endSwap, 140);
    }
    this.shownId = tower.id;

    const arch = tower.archetype;
    if (this.archetypeId !== arch.id) {
      this.archetypeId = arch.id;
      this.headIcon.replaceWith(
        (this.headIcon = iconFromId(arch.icon, FALLBACK_ICON, {
          size: 32,
          phosphor: arch.colour,
          glow: 0.9,
        })),
      );
      this.nameNode.textContent = arch.name;
      for (let p = 0; p < 3; p++) this.refreshPathHead(p as 0 | 1 | 2, arch);
    }

    this.tierNode.textContent = tower.tiers.join('-');
    // The damage type lives in the tooltip: spelled out in the row it wraps,
    // abbreviated it reads as noise.
    this.setStat('damage', round1(tower.stats.damage));
    const damageRow = this.statRows.get('damage');
    if (damageRow) damageRow.dataset.tip = `Damage — ${tower.stats.damageType} class, ${round1(tower.stats.damage)} per hit.`;
    this.setStat('rate', `${round1(tower.stats.fireRate)}/s`);
    this.setStat('range', `${Math.round(tower.stats.range)}px`);
    this.setStat('pierce', String(tower.stats.pierce));
    this.setStat('splash', tower.stats.splash > 0 ? `${Math.round(tower.stats.splash)}px` : '—');
    this.setStat('crit', tower.stats.critChance > 0 ? `${Math.round(tower.stats.critChance * 100)}%` : '—');
    this.dmgDealt.set(tower.damageDealt);
    this.killCount.set(tower.kills);

    const mode = tower.targeting;
    setIcon(this.targetIcon, TARGETING_ICON[mode]);
    this.targetLabel.textContent = TARGETING_LABEL[mode];

    const refund = this.rules.sellValue(tower);
    this.sellValue.textContent = money(refund);
    this.sellBtn.setAttribute('aria-label', `Sell ${arch.name} for ${refund} cash`);
    this.targetBtn.setAttribute('aria-label', `Targeting: ${TARGETING_LABEL[mode]}. Click to cycle.`);

    // The crossover rule is a fact about the tower, not about a path, and a
    // tower with two paths shut printed it twice — the same three lines, 39px
    // each time, which on a 1384×768 laptop with the shop docked was exactly
    // what pushed the Dealt and Kills counters below the fold. The first shut
    // path says it; the second keeps its `PATH CLOSED` banner and its tooltip.
    let toldRule = false;
    for (let p = 0; p < 3; p++) {
      toldRule = this.refreshPath(p as 0 | 1 | 2, tower, state.cash, !toldRule) || toldRule;
    }
  }

  destroy(): void {
    window.clearTimeout(this.swapTimer);
    this.bodyObserver?.disconnect();
    this.bodyObserver = null;
    this.sheetQuery?.removeEventListener('change', this.onLayout);
    this.sheetQuery = null;
    this.backBtn.removeEventListener('click', this.onBack);
    this.closeBtn.removeEventListener('click', this.onClose);
    this.targetBtn.removeEventListener('click', this.onTargeting);
    this.sellBtn.removeEventListener('click', this.onSell);
    for (const path of this.paths) {
      path.buy.removeEventListener('click', this.onBuy);
      for (const pip of path.pips) {
        pip.removeEventListener('click', this.onBuy);
        pip.removeEventListener('pointerenter', this.onPipExplain);
        pip.removeEventListener('pointerleave', this.onPipRest);
        pip.removeEventListener('focus', this.onPipExplain);
        pip.removeEventListener('blur', this.onPipRest);
      }
    }
    this.body?.removeEventListener('scroll', this.onBodyScroll);
    this.dmgDealt.destroy();
    this.killCount.destroy();
    this.paths.length = 0;
    this.root.remove();
    this.mounted = false;
  }

  // ─────────────────────────────────────────────────────────────── internals ──

  private statRow(key: string, name: IconName, label: string): HTMLElement {
    const row = el('div', 'stat');
    row.append(icon(name, { size: 12 }), el('span', 'stat__label', label));
    const value = el('span', 'stat__v num', '—');
    row.append(value);
    this.statValues.set(key, value);
    this.statRows.set(key, row);
    return row;
  }

  private setStat(key: string, text: string): void {
    const node = this.statValues.get(key);
    if (node) node.textContent = text;
  }

  private buildPath(index: 0 | 1 | 2): HTMLElement {
    const root = el('div', 'path');
    root.style.setProperty('--path-col', phosphorVar(PATH_COLOUR[index]));

    const headIcon = icon(PATH_FALLBACK_ICON[index], {
      size: 24,
      phosphor: PATH_COLOUR[index],
      glow: 0.7,
    });
    const label = el('span', 'path__label', `Path ${index + 1}`);

    const pipWrap = el('div', 'path__pips');
    const pips: HTMLButtonElement[] = [];
    const pipIcons: HTMLElement[] = [];
    for (let tier = 1; tier <= 5; tier++) {
      const pip = el('button', 'pip');
      pip.type = 'button';
      pip.dataset.path = String(index);
      pip.dataset.tier = String(tier);
      // Placeholder art only. `refreshPathArt` swaps in each tier's own icon
      // the moment an archetype arrives, so the strip teaches the path by
      // picture instead of showing five identical padlocks.
      const glyph = icon('lock', { size: 16, role: 'uiDisabled' });
      pip.append(glyph);
      pip.addEventListener('click', this.onBuy);
      /*
       * A pip explains itself into the row's own description line.
       *
       * It used to carry `data-tip` and open the floating bubble, which is the
       * one thing this panel cannot afford: the bubble is placed above the
       * anchor or, when there is no room above, below it — and below a pip is
       * the next path's row. Reported at 650x744 as a `Flechette Burst` bubble
       * drawn over `Open Choke` and its description, with the two sentences
       * interleaved. The row already owns a line for exactly this sentence, so
       * the pip writes there and the bubble never opens.
       */
      pip.addEventListener('pointerenter', this.onPipExplain);
      pip.addEventListener('pointerleave', this.onPipRest);
      pip.addEventListener('focus', this.onPipExplain);
      pip.addEventListener('blur', this.onPipRest);
      pips.push(pip);
      pipIcons.push(glyph);
      pipWrap.append(pip);
    }

    const head = el('div', 'path__head');
    head.append(headIcon, label, pipWrap);

    const buy = el('button', 'btn path__buy');
    buy.type = 'button';
    buy.dataset.path = String(index);
    const buyIcon = icon('shapes', { size: 12, colour: 'var(--ui-cash)' });
    const buyName = el('span', 'path__buy-name', '—');
    const buyCost = el('span', 'cost num', '');
    buy.append(buyIcon, buyName, buyCost);
    buy.addEventListener('click', this.onBuy);

    const state = el('div', 'path__state');
    const stateIcon = icon(STATE_ICON.closed, { size: 12, role: 'uiDisabled' });
    const stateLabel = el('span', undefined, 'PATH CLOSED');
    state.append(stateIcon, stateLabel);
    state.hidden = true;

    const desc = el('p', 'path__desc', '');

    root.append(head, buy, state, desc);
    this.paths.push({
      root, label, headIcon, pips, pipIcons,
      buy, buyIcon, buyName, buyCost, state, stateIcon, stateLabel, desc,
      descRest: '',
    });
    return root;
  }

  /**
   * Binds the archetype's artwork into the row: the path head borrows its first
   * upgrade's icon, and every pip takes its own tier's. The catalog has carried
   * these ids all along; the panel used to throw them away and draw a padlock.
   */
  private refreshPathHead(index: 0 | 1 | 2, arch: TowerArchetypeView): void {
    const ui = this.paths[index];
    const nodes = arch.paths[index];
    const first = nodes[0];
    const replacement = first
      ? iconFromId(first.icon, PATH_FALLBACK_ICON[index], {
          size: 24, phosphor: PATH_COLOUR[index], glow: 0.7,
        })
      : icon(PATH_FALLBACK_ICON[index], { size: 24, phosphor: PATH_COLOUR[index], glow: 0.7 });
    ui.headIcon.replaceWith(replacement);
    ui.headIcon = replacement;

    for (let tier = 1; tier <= 5; tier++) {
      const node = nodes[tier - 1];
      setIcon(
        ui.pipIcons[tier - 1],
        node ? safeIconId(node.icon, PATH_FALLBACK_ICON[index]) : PATH_FALLBACK_ICON[index],
      );
    }
  }

  /**
   * Repaints one path row. `sayRule` asks this row to be the one that spells the
   * crossover rule out, if it is shut; the return says whether it did.
   */
  private refreshPath(
    index: 0 | 1 | 2, tower: TowerView, cash: number, sayRule: boolean,
  ): boolean {
    const ui = this.paths[index];
    const nodes = tower.archetype.paths[index];
    const owned = tower.tiers[index];
    const allowed = this.rules.canPurchase(tower.tiers, index);
    const next = nodes[owned];

    ui.root.classList.toggle('is-blocked', !allowed && owned === 0);

    for (let tier = 1; tier <= 5; tier++) {
      const pip = ui.pips[tier - 1];
      const glyph = ui.pipIcons[tier - 1];
      const node = nodes[tier - 1];
      const purchased = tier <= owned;
      const isNext = tier === owned + 1;
      const affordable = node ? node.cost <= cash : false;

      pip.classList.toggle('is-purchased', purchased);
      pip.classList.toggle('is-available', !purchased && isNext && allowed && affordable);
      pip.classList.toggle('is-unaffordable', !purchased && isNext && allowed && !affordable);
      pip.classList.toggle('is-locked', !purchased && (!isNext || !allowed));

      // The artwork stays the tier's own throughout; the *state* is carried by
      // the cell around it — filled in the path colour when owned, ringed in
      // cash yellow when it is the next buy, red when it is out of reach. Two
      // channels, colour and fill, so neither has to work alone.
      glyph.style.setProperty(
        '--pxi-col',
        purchased
          ? phosphorVar(PATH_COLOUR[index])
          : isNext && allowed && affordable
            ? 'var(--ui-cash)'
            : 'var(--ui-disabled)',
      );
      glyph.style.setProperty('--pxi-glow', purchased ? '0.8' : '0');

      pip.setAttribute(
        'aria-label',
        node
          ? `Path ${index + 1} tier ${tier}: ${node.name}, ${node.cost} cash`
          : `Path ${index + 1} tier ${tier}`,
      );
      // Read by `onPipExplain` into the row's description line. Not `data-tip`:
      // see the listener wiring in `buildPath` for why this row cannot float.
      if (node) pip.dataset.explain = `${node.name} — ${node.description}`;
      else delete pip.dataset.explain;
      // An upgrade you cannot yet afford stays focusable and keeps its tooltip:
      // reading what 1 920 buys is half of deciding to save for it. Only truly
      // unreachable tiers leave the tab order.
      const reachable = !purchased && isNext && allowed;
      pip.disabled = !reachable;
      pip.setAttribute('aria-disabled', String(reachable && !affordable));
    }

    // The row names what the tower already *is* on this path. The buy control
    // below names what it would become. Between them a player reads the whole
    // decision without moving the pointer.
    const top = owned > 0 ? nodes[owned - 1] : undefined;
    ui.label.textContent = top ? top.name : `Path ${index + 1}`;
    ui.label.classList.toggle('is-owned', owned > 0);
    // Nothing bought and something to buy: the row below carries the name, so
    // the grey `Path 3` above it is a heading over its own answer. It goes, and
    // the pip strip takes the line. A shut path keeps it — `PATH CLOSED` needs
    // a subject.
    ui.root.classList.toggle('is-unnamed', owned === 0 && next !== undefined && allowed);

    if (next && allowed) {
      ui.buy.hidden = false;
      ui.state.hidden = true;
      setIcon(ui.buyIcon, safeIconId(next.icon, PATH_FALLBACK_ICON[index]));
      ui.buyName.textContent = next.name;
      ui.buyCost.textContent = money(next.cost);
      ui.buy.disabled = false;
      ui.buy.setAttribute('aria-disabled', String(next.cost > cash));
      ui.buy.classList.toggle('is-unaffordable', next.cost > cash);
      ui.buy.dataset.tip = `${next.name} — ${next.description}`;
      ui.buy.setAttribute(
        'aria-label',
        `Buy ${next.name} on path ${index + 1} for ${next.cost} cash`,
      );
      this.setDesc(ui, next.description);
      return false;
    } else {
      // Not a disabled button. A shut path and a finished path are different
      // outcomes, and each gets a banner that says which, in words, at 11px.
      ui.buy.hidden = true;
      ui.buy.disabled = true;
      ui.buy.classList.remove('is-unaffordable');
      ui.state.hidden = false;

      const maxed = owned >= 5;
      setIcon(ui.stateIcon, maxed ? STATE_ICON.maxed : STATE_ICON.closed);
      ui.stateLabel.textContent = maxed ? 'MAX UPGRADES' : 'PATH CLOSED';
      ui.state.classList.toggle('is-maxed', maxed);
      // The crossover rule stops being a tooltip and becomes the caption — on
      // the first shut path. The banner keeps it as a tooltip either way, so a
      // player asking of *this* row why it is shut still gets the answer.
      ui.state.dataset.tip = maxed ? MAXED_TEXT : CROSSOVER_TEXT;
      this.setDesc(ui, maxed ? MAXED_TEXT : sayRule ? CROSSOVER_TEXT : '');
      return !maxed && sayRule;
    }
  }

  /**
   * Writes the row's resting sentence, and remembers it.
   *
   * Every refresh comes through here, so a pip that is being pointed at while
   * the panel repaints does not get its borrowed text overwritten by the row's
   * own and then restored to the wrong thing on the way out.
   */
  private setDesc(ui: PathUi, text: string): void {
    ui.descRest = text;
    if (ui.desc.dataset.borrowed === 'true') return;
    ui.desc.textContent = text;
    ui.desc.hidden = text === '';
  }

  /**
   * Lights the end the document can still travel to.
   *
   * Measured rather than assumed, on every scroll and on every repaint: the
   * panel's content changes size when a path opens or closes, so a marker
   * decided once would go on claiming there was more after the last upgrade
   * made the document fit.
   *
   * Which *axis* is measured is a question now. The panel is a bar in every
   * landscape layout and a sheet in portrait, so the document runs sideways in
   * one and downwards in the other. Taken rather than assumed, the way
   * `ShopPanel.syncOverflow` takes it and for the same reason: an arrow
   * pointing along an axis that does not move is a lie told confidently.
   */
  private readonly onBodyScroll = (): void => {
    const b = this.body;
    if (!b) return;
    const spanX = b.scrollWidth - b.clientWidth;
    const spanY = b.scrollHeight - b.clientHeight;
    const x = spanX >= spanY;
    const room = x ? spanX : spanY;
    const at = x ? b.scrollLeft : b.scrollTop;
    this.scroll.classList.toggle('has-more-start', room > 2 && at > 2);
    this.scroll.classList.toggle('has-more-end', room > 2 && at < room - 2);
  };

  /**
   * Puts the tower's mark, its numbers and its counters where the layout wants
   * them, and does nothing at all if they are already there.
   *
   * Order matters in both homes and is not the same order: the sheet reads
   * downwards and puts the live counters under the paths, where they read as
   * the document's footer; the bar reads the meta column downwards and puts
   * them under the reference stats, where they read as two more numbers about
   * the same tower. Neither box holds a focusable control, so nothing is torn
   * out of the tab order or out from under a pointer by the move.
   */
  private readonly onLayout = (): void => {
    const sheet = this.sheetQuery?.matches === true;
    if (sheet) {
      this.body.append(this.head, this.stats, this.pathWrap, this.live);
    } else {
      this.meta.append(this.head, this.stats, this.live);
      this.body.append(this.pathWrap);
    }
    queueMicrotask(this.onBodyScroll);
  };

  /** The pointed-at pip takes the row's line. */
  private readonly onPipExplain = (ev: Event): void => {
    const pip = ev.currentTarget;
    if (!(pip instanceof HTMLElement)) return;
    const text = pip.dataset.explain;
    const ui = this.paths[Number(pip.dataset.path)];
    if (!ui || !text) return;
    ui.desc.dataset.borrowed = 'true';
    ui.desc.textContent = text;
    ui.desc.hidden = false;
  };

  /** And gives it back. */
  private readonly onPipRest = (ev: Event): void => {
    const pip = ev.currentTarget;
    if (!(pip instanceof HTMLElement)) return;
    const ui = this.paths[Number(pip.dataset.path)];
    if (!ui) return;
    delete ui.desc.dataset.borrowed;
    ui.desc.textContent = ui.descRest;
    ui.desc.hidden = ui.descRest === '';
  };

  private readonly onBuy = (ev: Event): void => {
    const target = ev.currentTarget;
    if (!(target instanceof HTMLElement)) return;
    const path = Number(target.dataset.path) as 0 | 1 | 2;
    const tower = this.state.tower;
    if (!tower || Number.isNaN(path)) return;
    if (!this.rules.canPurchase(tower.tiers, path)) return;
    // The control stays live so it can be read; the cash check happens here.
    const next = tower.archetype.paths[path][tower.tiers[path]];
    if (!next || next.cost > this.state.cash) return;
    this.cb.onUpgrade?.(tower.id, path);
  };

  private readonly onTargeting = (ev: MouseEvent): void => {
    const tower = this.state.tower;
    if (!tower) return;
    const i = TARGETING_ORDER.indexOf(tower.targeting);
    const step = ev.shiftKey ? -1 : 1;
    const n = TARGETING_ORDER.length;
    const next = TARGETING_ORDER[(i + step + n) % n];
    this.cb.onTargeting?.(tower.id, next);
  };

  private readonly onSell = (): void => {
    const tower = this.state.tower;
    if (tower) this.cb.onSell?.(tower.id);
  };

  private readonly endSwap = (): void => {
    this.root.classList.remove('is-swapping');
  };

  private readonly onBack = (): void => {
    this.cb.onBack?.();
  };

  private readonly onClose = (): void => {
    this.cb.onClose?.();
  };
}

function round1(v: number): string {
  return (Math.round(v * 10) / 10).toString();
}

/**
 * `18.4k`. Grouped digits pair badly with a neighbouring stat — the thin space
 * reads as a column break — so the live counters go compact instead.
 */
function compact(v: number): string {
  const n = Math.round(v);
  if (n < 10000) return String(n);
  if (n < 1000000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1000000).toFixed(2)}M`;
}
